import cn from "classnames";
import { useInView } from "react-intersection-observer";
import PropTypes from "prop-types";

import { MapItinerary } from "@icons";

import { ImgixNextImage as Image } from "@components/imgixNextImage";

import { SaveButtonCircle } from "@pageComponents/itineraries/saveButtonCircle";

import { useScreens } from "@utils/component";

import { tagular } from "@cohesion/tagular";
import { Meta } from "./meta";
import { ContentContainer } from "./content";

const sizes = {
  sm: { w: 432, h: 324 },
  md: { w: 642, h: 428 },
  lg: { w: 768, h: 512 },
  xl: { w: 960, h: 640 },
  "2xl": { w: 960, h: 640 },
};

export function Detail({
  index,
  esid,
  title,
  slug,
  meta,
  image = null,
  editorial,
  handleMapInteraction,
  className = "",
}) {
  const { screen } = useScreens();
  const { ref: inViewRef, inView } = useInView({
    threshold: 1,
    triggerOnce: true,
    rootMargin: "50px 0px 0px 0px",
  });
  const onPoiTitleClick = () => {
    tagular("click", {
      actionOutcome: "INTERNALLINK",
      outboundUrl: "null",
      webElement: {
        elementType: "LINK",
        location: "ITINERARYDAY",
        name: `${title}`,
        position: "3",
        text: `${title}`,
      },
    });
  };

  return (
    <div ref={inViewRef}>
      <div
        className={cn(
          "md:container lg:px-0 lg:max-w-7xl",
          image?.src && index !== 0 && "pt-12 md:pt-16",
          !image?.src && index !== 0 && "py-12 md:py-16",
          !image?.src && index === 0 && "pb-12 md:pb-16",
          className
        )}
      >
        <div className="grid grid-cols-4">
          <div className="hidden text-left lg:block">
            <span className="font-mencken font-regular text-blue-100 leading-[50%] text-[300px] cursor-default select-none">
              {index + 1}
            </span>
          </div>

          <div className="col-span-4 lg:col-span-3">
            {image?.src && (
              <Image
                className=" w-full md:rounded"
                src={image.src}
                width={sizes[screen || "sm"].w}
                height={sizes[screen || "sm"].h}
                imgixParams={{ ...sizes[screen], fit: "crop" }}
              />
            )}
            <article
              className={cn(
                "overflow-x-hidden md:w-90 md:mx-auto mx-2 shadow-md border border-transparent-20 rounded bg-white p-4 md:p-8",
                image?.src && "-translate-y-12 pb-4"
              )}
            >
              <header
                className={cn(
                  "mb-4",
                  !meta?.address &&
                    "md:flex md:justify-between md:flex-row-reverse"
                )}
              >
                <div className="flex flex-col gap-2 md:flex-row md:items-center">
                  <div className="w-full">
                    <Meta {...meta} />
                  </div>
                  <div className="flex flex-row justify-between">
                    <div className="md:hidden">
                      <h3 className="text-xl font-semibold md:text-2xl">
                        <a
                          href={`/${slug}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={onPoiTitleClick}
                        >
                          {title}
                        </a>
                      </h3>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 md:justify-end">
                        <button
                          type="button"
                          className="flex items-center gap-2 p-2 text-sm font-semibold text-white rounded-pill bg-blue md:px-4"
                          onClick={e => {
                            handleMapInteraction(e, esid);
                            tagular("click", {
                              actionOutcome: "SELECT",
                              outboundUrl: "null",
                              webElement: {
                                elementType: "BUTTON",
                                location: "ITINERARYDAY",
                                name: "MAP",
                                position: "0",
                                text: "POI MAP",
                              },
                            });
                          }}
                        >
                          <MapItinerary className="text-md" />
                          <span className="hidden md:block">Map</span>
                        </button>

                        <SaveButtonCircle esid={esid} type="poi" slug={slug} />
                      </div>
                    </div>
                  </div>
                </div>
                <h3 className="hidden font-semibold md:block md:text-2xl md:mt-2">
                  <a
                    href={`/${slug}`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={onPoiTitleClick}
                    className="text-black hover:text-blue hover:underline"
                  >
                    {title}
                  </a>
                </h3>
              </header>

              <ContentContainer
                inView={inView}
                data={{ slug, editorial, meta, title }}
              />
            </article>
          </div>
        </div>
      </div>
    </div>
  );
}

Detail.propTypes = {
  index: PropTypes.number.isRequired,
  image: PropTypes.shape({
    src: PropTypes.string,
  }),
  title: PropTypes.string.isRequired,
  meta: PropTypes.shape({
    address: PropTypes.string,
    coords: PropTypes.shape({ lat: PropTypes.number, lon: PropTypes.number }),
    parent: PropTypes.shape({
      slug: PropTypes.string,
    }),
    tags: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  esid: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  editorial: PropTypes.string.isRequired,
  handleMapInteraction: PropTypes.func.isRequired,
  className: PropTypes.string,
};
